import {
  createRef, useEffect, useRef, useState,
} from 'react';
import { throttle } from 'lodash-es';
import ScrollContainer from 'react-indiana-drag-scroll';
import { generateClassName, getColorVariantFromBackground, setHeaderTag } from '@klv/shared/utils/functions';
import Buttons from '@klv/molecules/buttons';
import Icon from '@klv/atoms/icon';
import GalleryCard from '@klv/molecules/gallery-card';
import {
  getColumnGap, getGalleryPadding, handleCardFocus, handleCarouselClick, scrollDistanceValue, updateShowNext, updateShowPrevious,
} from './utils/functions';
import { carouselButtonLabels, defaultScrollDistance } from './utils/variables';

import * as s from './styles/gallery.module.scss';

import type { TColorVariant } from '@klv/shared/styles/types';
import type { IGallery } from './types';

const Gallery = ({
  backgroundColor,
  buttons = [],
  galleryCards = [],
  headline = '',
  showCardTitles = false, subhead = '',
  uniformCardButtonText = 'View',
  ...rest
}: IGallery & React.HTMLAttributes<HTMLDivElement>) => {
  /** Start: Standard variables */
  const colorVariant: TColorVariant = getColorVariantFromBackground(backgroundColor);
  /** End: Standard variables */

  /** Start: Ref variables */
  const cardsRef = useRef<React.RefObject<HTMLDivElement>[]>(galleryCards.map(() => createRef()));
  const cardsWrapperRef = useRef<HTMLDivElement>(null);
  const columnGap = useRef<number>(24);
  const galleryPadding = useRef<number>(0);
  const scrollBox = useRef<HTMLDivElement>(null);
  const scrollDistance = useRef<number>(defaultScrollDistance);
  /** End: Ref variables */

  /** Start: State variables */
  const [showNext, setShowNext] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);
  /** End: State variables */

  /** Start: Function variables */
  const updateStates = () => {
    const setCarouselButtonProps = {
      columnGap: columnGap.current,
      scrollBox: scrollBox.current,
    };

    setShowNext(updateShowNext({ ...setCarouselButtonProps, cardsRef: cardsRef.current }));
    setShowPrevious(updateShowPrevious(setCarouselButtonProps));
    scrollDistance.current = scrollDistanceValue({ cardsRef: cardsRef.current, columnGap: columnGap.current });
  };

  const carouselScroll = () => {
    columnGap.current = getColumnGap(scrollBox.current); // Run in case the default value is no longer correct.
    galleryPadding.current = getGalleryPadding();

    updateStates();
  };

  const galleryWindowResize = throttle(() => {
    updateStates();
  }, 200);
  /** End: Function variables */

  useEffect((): () => void | undefined => {
    if (scrollBox.current) {
      window.addEventListener('resize', galleryWindowResize);
      updateStates();

      return () => {
        window.removeEventListener('resize', galleryWindowResize);
      };
    }

    return () => undefined;
  }, [scrollBox, galleryWindowResize]);

  const IntroText = setHeaderTag(headline) || 'div';

  const showCarouselButton = (label: 'previous' | 'next') => (label === 'next' ? showNext : showPrevious);

  return (
    <div
      className={generateClassName([
        s.gallery,
        s[backgroundColor],
        'defaultPaddingTop',
        'defaultPaddingBottom',
      ])}
      {...rest}
    >
      {(buttons?.length || headline || subhead) && (
      <div className={generateClassName([s.introContent, 'grid'])}>
        {(headline || subhead) && (
        <IntroText className={generateClassName([s.introText, 'grid'])}>
          {headline && (
            <h2 className={generateClassName([s.headline, s[colorVariant], 'h3'])}>
              {headline}
            </h2>
          )}
          {subhead && (
            <div
              className={generateClassName([s.subhead, s[colorVariant], 'p2'])}
              dangerouslySetInnerHTML={{ __html: subhead }}
            />
          )}
        </IntroText>
        )}
        {buttons?.length && (
          <Buttons
            className={s.buttons}
            buttonColor={colorVariant}
            buttons={buttons}
            variants={['primary']}
          />
        )}
      </div>
      )}
      <div className={s.carousel}>
        {carouselButtonLabels.map((label) => (
          <button
            className={generateClassName([
              s.carouselButton,
              showCarouselButton(label) && s.show,
              label === 'next' && s.next,
              label === 'previous' && s.previous,
            ])}
            aria-hidden="true"
            key={label}
            onClick={() => {
              handleCarouselClick({
                carousel: scrollBox.current,
                direction: label,
                scrollDistance,
              });
            }}
            tabIndex={-1}
          >
            <Icon
              className={s.buttonIcon}
              fillColor="currentColor"
              iconName="ChevronDown"
            />
          </button>
        ))}
        <div className={s.galleryCards} ref={cardsWrapperRef}>
          <ScrollContainer
            className={s.galleryScrollBox}
            innerRef={scrollBox}
            onScroll={carouselScroll}
            vertical={false}
          >
            {galleryCards.map((card, index) => (
              <GalleryCard
                backgroundColor={backgroundColor}
                cardButtonText={uniformCardButtonText}
                innerRef={cardsRef.current[index]}
                key={JSON.stringify(card)}
                onFocus={(() => {
                  handleCardFocus({
                    card: cardsRef?.current?.[index]?.current ?? undefined,
                    galleryPadding: galleryPadding.current,
                    scrollBox: scrollBox.current,
                  });
                })}
                showTitle={showCardTitles}
                {...card.galleryCard}
              />
            ))}
          </ScrollContainer>
        </div>
      </div>
    </div>
  );
};

export default Gallery;
